import { useEffect, useState } from "react";
import { Box, Tab, Tabs, Typography, useTheme, Tooltip } from "@mui/material";
import AgGridTable from "../../components/table/AgGridTable";
import { ColDef, GridOptions, ICellRendererParams } from "ag-grid-community";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import paloalto_dark from "../../assets/paloalto-white.svg";
// import cisco_dark from " ../../assets/cisco-white.svg";
import cisco_dark from "../../assets/cisco-white.svg";

import arista_dark from "../../assets/arista-white.svg";
import juniperLogo from "../../assets/juniper.svg";
import juniper_dark from "../../assets/juniper-white.svg";
import cisco_assets from "../../assets/cisco-assets.svg";
import arista_assets from "../../assets/arista-assets.svg";
import paloalto_assets from "../../assets/paloalto-assets.svg";
import cveBug from "../../assets/cve-bug.svg";
import green_shield from "../../assets/green-shield.svg";
import red_shield from "../../assets/red-shield.svg";
import orange_shield from "../../assets/orange-shield.svg";
import misconfig from "../../assets/misconfig.svg";
// Removed imports for calendar icons and LaunchSharpIcon
import LaunchSharpIcon from "@mui/icons-material/LaunchSharp"; // Keep LaunchSharpIcon if used elsewhere
import { useNavigate } from "react-router-dom";
// Import Aws_img
import Aws_img from "../../assets/baseVersion/aws.svg"; // Corrected import path and filename
import AWS_DARK from "../../assets/baseVersion/AWS_DARK.svg"
import apple_img from "../../assets/apple.svg";
import apple_dark from "../../assets/apple_dark.svg";

const TotalAssets = () => {
  const [assetsData, setAssetsData] = useState<any[]>([]);
  const [tab, setTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [assetCounts, setAssetCounts] = useState<{
    onPremise: number;
    cloud: number;
  }>({ onPremise: 0, cloud: 0 });
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const navigate = useNavigate();

  const formatSecurityScore = (score: number): number => {
    if (isNaN(score)) return 0;

    // 0 → 0.5  => 0
    // 0.6 → 1  => 1
    return Math.round(score);
  };

  // Define column definitions for On-prem assets
  const onPremColumnDefs: ColDef[] = [
    {
      field: "device_name",
      headerName: "Asset Name",
      filter: false,
      sortable: false,
      minWidth: 150,
    },

    {
      field: "vendor",
      headerName: "Vendor",
      filter: true,
      minWidth: 120,
      cellRenderer: (params: ICellRendererParams) =>
        params.data.vendor.toLowerCase() === "cisco" ? (
          <img
            src={isDarkMode ? cisco_dark : cisco_assets}
            alt="Cisco"
            style={{ width: "60px", height: "20px" }}
          />
        ) : params.data.vendor.toLowerCase() === "juniper" ? (
          <img
            src={isDarkMode ? juniper_dark : juniperLogo}
            alt="Juniper"
            style={{ width: "60px", height: "20px" }}
          />
        ) : params.data.vendor === "paloalto" ||
          params.data.vendor === "Palo Alto" ||
          params.data.vendor === "palo alto" ? (
          <img
            src={isDarkMode ? paloalto_dark : paloalto_assets}
            alt="Palo Alto"
            style={{ width: "60px", height: "20px" }}
          />
        ) : params.data.vendor.toLowerCase() === "arista" ? (
          <img
            src={isDarkMode ? arista_dark : arista_assets}
            alt="Arista"
            style={{ width: "60px", height: "20px" }}
          />
        ) : params.data.vendor.toLowerCase() === "apple" || params.data.vendor.toLowerCase() === "macos" ? (
          <img
            src={isDarkMode ? apple_dark : apple_img}
            alt="Apple"
            style={{ width: "32px", height: "32px", objectFit: "contain" }}
          />
        ) : (
          <Typography
            title={params.data.vendor}
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {params.data.vendor}
          </Typography>
        ),
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
    {
      field: "model",
      headerName: "OS",
      filter: true,
      minWidth: 120,
    },
    {
      field: "device_type",
      headerName: "Type",
      filter: true,
      sortable: true,
      minWidth: 150,
    },
    {
      field: "security_score",
      headerName: "Security Score",
      filter: false,
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams) => {
        return params.data?.status === 3 ? (
          <Tooltip
            title={`Security Score:${Number(params.data.security_score).toFixed(2)}`}
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#fff",
                  color: "#000",
                  boxShadow: 1,
                  fontSize: "0.775rem",
                },
              },
              arrow: {
                sx: {
                  color: "#fff",
                },
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {params.data.security_score <= 50 && (
                <img src={red_shield} alt="Low Security Score" />
              )}
              {params.data.security_score > 50 &&
                params.data.security_score < 80 && (
                  <img src={orange_shield} alt="Medium Security Score" />
                )}
              {params.data.security_score >= 80 && (
                <img src={green_shield} alt="High Security Score" />
              )}

              <Typography>
                {formatSecurityScore(Number(params.data.security_score))}
              </Typography>

            </Box>
          </Tooltip>
        ) : (
          <span>
            {/* <LoopIcon sx={{ color: "text.secondary", fontSize: 16 }} /> */}
            <span>Scanning...</span>
          </span>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
    // {
    //     field: 'misconfigurations',
    //     headerName: 'Misconfigurations ',
    //     filter: true,
    //     minWidth: 150,
    //     valueFormatter: (params) => {
    //         return params.value ? new Date(params.value).toLocaleString() : 'Never';
    //     }
    // },
    {
      field: "vulnerability_levels.failed_count",
      headerName: "Misconfigurations",
      filter: false,
      sortable: true,
      minWidth: 180,
      //  valueGetter: (params: any) => {
      //   // Make sure AG Grid sees this number for filtering and sorting
      //   return params.data?.status === 3 ? params.data?.failed_count ?? 0 : 0;
      // },
      cellRenderer: (params: ICellRendererParams) => {
        return params.data?.status === 3 ? (
          <Box display="flex" alignItems="center" gap={1}>
            {params.data?.vulnerability_levels?.failed_count >= 0 && (
              <img
                src={misconfig}
                alt="Misconfigurations"
                style={{ width: 20, height: 20 }}
              />
            )}
            <Typography>
              {Math.round(params.data?.vulnerability_levels?.failed_count)}
            </Typography>
          </Box>
        ) : (
          <span>
            {/* <LoopIcon sx={{ color: "text.secondary", fontSize: 16 }} /> */}
            <span>Scanning...</span>
          </span>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
    {
      field: "cve_score",
      headerName: "CVE Score",
      filter: false,
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams) => {
        return params.data?.status === 3 ? (
          <Tooltip
            title={`CVE Score: ${params.data?.cve_score}`}
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#fff",
                  color: "#000",
                  boxShadow: 1,
                  fontSize: "0.775rem",
                },
              },
              arrow: {
                sx: {
                  color: "#fff",
                },
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <img
                src={cveBug}
                alt="Cve Score"
                style={{ width: 20, height: 20 }}
              />
              <Typography>{Math.round(params.data?.cve_score)}</Typography>
            </Box>
          </Tooltip>
        ) : (
          ""
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
  ];

  // Define column definitions for Cloud assets
  const cloudColumnDefs: ColDef[] = [
    {
      field: "resource_name",
      headerName: "Name",
      filter: false,
      sortable: false,
      minWidth: 300,
      width: 330,
      cellRenderer: (params: ICellRendererParams) => {
        const criticalityColors: any = {
          critical: "firebrick",
          high: "orange",
          low: "limegreen",
          default: "goldenrod",
        };

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <div
              style={{
                minWidth: "4px",
                width: "4px",
                height: "32px",
                backgroundColor:
                  criticalityColors[params?.data?.criticality?.toLowerCase()] ||
                  "goldenrod",
                borderRadius: "2px",
                marginRight: "8px",
                cursor: "pointer",
                flexShrink: 0, // Prevent the bar from shrinking
                position: "relative", // Ensure proper stacking
                zIndex: 1, // Keep the bar above other elements
              }}
            ></div>
            <Typography
              title={params.data.resource_name}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                cursor: "pointer",
                flexGrow: 1,
                minWidth: 0,
                maxWidth: "160px", // ✅ set a max width
              }}
            >
              {params.data.resource_name}
            </Typography>
          </Box>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
    {
      field: "cloud",
      headerName: "Vendor",
      filter: true,
      minWidth: 180,
      width: 180,
      cellRenderer: (params: ICellRendererParams) =>
        params.data.vendor?.toLowerCase() === "cisco" ? (
          <img
            src={isDarkMode ? cisco_dark : cisco_assets}
            alt="Cisco"
            style={{ width: "60px", height: "20px" }}
          />
        ) : params.data.vendor?.toLowerCase() === "juniper" ? (
          <img
            src={isDarkMode ? juniper_dark : juniperLogo}
            alt="Juniper"
            style={{ width: "60px", height: "20px" }}
          />
        ) : params.data.vendor?.toLowerCase() === "paloalto" ||
          params.data.vendor?.toLowerCase() === "Palo Alto" ? (
          <img
            src={isDarkMode ? paloalto_dark : paloalto_assets}
            alt="Palo Alto"
            style={{ width: "60px", height: "20px" }}
          />
        ) : params.data.vendor?.toLowerCase() === "arista" ? (
          <img src={arista_assets} alt="Arista" />
        ) : params.data.vendor?.toLowerCase() === "apple" || params.data.vendor?.toLowerCase() === "macos" ? (
          <img
            src={isDarkMode ? apple_dark : apple_img}
            alt="Apple"
            style={{ width: "32px", height: "32px", objectFit: "contain" }}
          />
        ) : // Fallback to cloud provider image if vendor is not matched
          params.data.cloud?.toLowerCase() === "amazon web services" ? (
            <img src={isDarkMode ? AWS_DARK : Aws_img} alt="AWS" />
          ) : // Fallback to displaying the cloud name if no image is available
            params.data.cloud ? (
              <Typography
                title={params.data.cloud}
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {params.data.cloud}
              </Typography>
            ) : // Fallback to displaying the vendor name if cloud name is not available
              params.data.vendor ? (
                <Typography
                  title={params.data.vendor}
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params.data.vendor}
                </Typography>
              ) : null, // Display nothing if both cloud and vendor are not available
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
    {
      field: "resources_type",
      headerName: "Resource Type",
      filter: true,
      minWidth: 200,
      width: 240,
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
  ];

  const gridOptions: GridOptions = {
    defaultColDef: {
      sortable: true,
      resizable: true,
    },
  };

  // NOTE: Previously fetched via GET from END_POINTS.DASHBOARD_ASSETS_DETAILS ('securityscan/dashboard-assets-details').
  // Backend removed 'dashboard-assets-details' API and consolidated the logic into 'dashboard-score' (END_POINTS.DASHBOARD_SCORE).
  // It is now called using POST with payload { module: "dashboard-assets-details" }.
  const fetchAssetCounts = async () => {
    try {
      const response = await axiosInstance.post(END_POINTS.DASHBOARD_SCORE, {
        module: "dashboard-assets-details",
      });
      if (response?.status === 200) {
        setAssetCounts({
          onPremise: response?.data?.data?.on_premise_asset_count || 0,
          cloud: response?.data?.data?.cloud_asset_count || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching asset counts:", error);
    }
  };

  const fetchAssetsData = async () => {
    try {
      setIsLoading(true);

      let endpoint = END_POINTS.DEVICE_LIST;
      let payload = {};

      if (tab === 1) {
        endpoint = END_POINTS.DEVICE_LIST_CLOUD;
        payload = { type: 1 };
      }

      const response = await axiosInstance.post(endpoint, payload);
      if (tab !== 1 && response.data) {
        setAssetsData(response.data?.data?.device_list || []);
      } else if (response.data) {
        // Filter cloud resources based on last_scan_status
        const cloudResources = response.data?.data?.cloud_resource || [];
        const filteredCloudResources = cloudResources.filter((resource: any) =>
          resource.last_scan_status === true
        );
        setAssetsData(filteredCloudResources);
      }
    } catch (error) {
      console.error("Error fetching assets data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAssetCounts();
  }, []);

  useEffect(() => {
    fetchAssetsData();
  }, [tab]);

  useEffect(() => {
    // Adjust tab selection based on available asset counts
    if (assetCounts.onPremise > 0 && assetCounts.cloud === 0) {
      setTab(0); // On-prem only
    } else if (assetCounts.onPremise === 0 && assetCounts.cloud > 0) {
      setTab(1); // Cloud only
    }
  }, [assetCounts]);

  return (
    <>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 0.5 }}>
        {assetCounts.onPremise > 0 && (
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography fontSize="14px">On-prem</Typography>
                {/* <img
                  src={Layer123}
                  alt="Layer123"
                  style={{ height: 22, cursor: "pointer" }}
                  onClick={() => navigate("/on-premises/security-posture")}
                /> */}
                <LaunchSharpIcon
                  sx={{
                    color: isDarkMode ? "white" : "black",
                    cursor: "pointer",
                    fontSize: "17px",
                  }}
                  onClick={() => navigate("/on-premises/assets")}
                />
              </Box>
            }
          />
        )}
        {assetCounts.cloud > 0 && (
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography fontSize="14px">Cloud</Typography>
                {/* <img
                  src={Layer123}
                  alt="Layer123"
                  style={{ height: 22, cursor: "pointer" }}
                  onClick={() => navigate("/cloud/security-posture")}
                /> */}
                <LaunchSharpIcon
                  sx={{
                    color: isDarkMode ? "white" : "black",
                    cursor: "pointer",
                    fontSize: "17px",
                  }}
                  onClick={() => navigate("/cloud/cloud-resources")}
                />
              </Box>
            }
          />
        )}
      </Tabs>
      <AgGridTable
        data={assetsData}
        columnDefs={
          (assetCounts.onPremise > 0 && assetCounts.cloud === 0) ||
            (assetCounts.onPremise > 0 && assetCounts.cloud > 0 && tab === 0)
            ? onPremColumnDefs
            : cloudColumnDefs
        }
        isLoading={isLoading}
        gridOptions={gridOptions}
        showSearch={true}
        height={"calc(100vh - 130px)"}
      />
    </>
  );
};

export default TotalAssets;
